// Phase 74.1 — stale chunk auto-recovery.
//
// When we deploy, Vite gives route chunks new hashed filenames and removes the
// old ones. A browser tab opened before the deploy still references the old
// names, so navigating to a lazy-loaded route fails with "Failed to fetch
// dynamically imported module". The fix: detect that specific failure and
// reload once so the tab pulls the fresh index.html and silently lands on the
// route it was navigating to. A once-per-window guard (sessionStorage) means a
// genuinely broken deploy shows the error screen instead of looping forever.

const RELOAD_AT_KEY = 'sc:stale_chunk_reload_at'
const RELOAD_WINDOW_MS = 10_000

// Browser/bundler phrasings for "a dynamic import() could not be loaded".
// Matched case-insensitively. Chrome, Firefox, and Safari each word it
// differently, so we cover all of them.
const DYNAMIC_IMPORT_MESSAGES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to import',
]

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message: unknown }).message
    if (typeof m === 'string') return m
  }
  return ''
}

/**
 * True when the error is a failed dynamic import (a stale route chunk after a
 * deploy), as opposed to a real application error.
 */
export function isDynamicImportError(err: unknown): boolean {
  const lower = messageOf(err).toLowerCase()
  if (!lower) return false
  return DYNAMIC_IMPORT_MESSAGES.some((m) => lower.includes(m))
}

/**
 * Reload the page once to recover from a stale chunk, guarded so a real chunk
 * 404 (the asset is truly gone on the host) cannot infinite-loop. Returns true
 * when it triggered a reload (caller should render a transitional state), false
 * when it already reloaded within the guard window (caller should fall through
 * to the normal error UI).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? '0')
    if (last && Date.now() - last < RELOAD_WINDOW_MS) {
      // Already reloaded once very recently and still failing: this is a real
      // failure (or a broken deploy). Do not loop; let the error screen show.
      return false
    }
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode, blocked storage). Without a
    // cross-reload guard we accept a single best-effort reload attempt.
  }
  window.location.reload()
  return true
}
